import getLogger from '@ignitesales/logger-es';
import { ignite } from '@ignitesales/neuro-application';
import queryString from 'query-string';
import commerceProductMap, { idByCategory } from '../../../../../theme/src/main/js/util/commerceProductMap';
import decodeUriComponent from 'decode-uri-component';

const logger = getLogger({ module: 'saveParameters' });

const productMap = Object.keys(idByCategory).reduce((obj, key) => {
  idByCategory[key].map((product) => {
    return obj.set(product, key);
  });
  return obj;
}, new Map());

export const parameterOptions = {
  persistentKey: 'customerId',
  email: 'email',
  accountInfo: 'existingEnactCustomerProducts',
};

export const balanceAppKeys = new Map([
  ['checking', 'averageMonthlyCheckingBalance'],
  ['savings', 'averageMonthlySavingsBalance'],
  ['cd', 'averageMonthlyCDBalance'],
  ['retirement', 'averageMonthlyRetirementBalance'],
]);

/**
 * When a session is initialized, we are sent a list of existing products a customer has.
 * We need the ignite product ids saved to the existing products, instead.
 *
 * This code will map the commerce id to the ignite id and save it as an answer.
 *
 * If a balance is associated with the product and the product falls into one of four categories,
 * (checking, savings, cd, retirement)
 * then we also save that balance as an answer.
 */
const mapEnactProductsToIgniteProducts = (products) => {
  let igniteProductIds = new Set();
  let igniteAverageBalances = new Map();

  if (Array.isArray(products)) {
    products.forEach((product) => {
      // Must wrap in string because Answer Service won't accept mixed num/string arrays
      const productId =
        product[0] && (commerceProductMap[product[0]] || `${product[0]}`);
      if (!productId) return;

      igniteProductIds.add(productId.toString());

      const balance = product[1] != null && product[1];
      const balanceCategory = productMap.get(product[0]);
      const balanceAppKey = balanceAppKeys.get(balanceCategory);
      if (isNaN(balance) || !balanceCategory || !balanceAppKey) return;
      let currentCategoryBalance = igniteAverageBalances.get(balanceAppKey);
      const updatedCategoryBalance = (isNaN(currentCategoryBalance)) ? balance : currentCategoryBalance + balance;
      igniteAverageBalances.set(balanceAppKey, updatedCategoryBalance);
    });
  }

  logger.info('Found Ignite product ids', igniteProductIds, igniteAverageBalances);

  return {
    existingCustomerProducts: [...igniteProductIds],
    existingCustomerBalances: igniteAverageBalances,
  };
};

export const saveParameters = async () => {
  let enactProductBalanceMap;
  const toSave = new Set();

  const addToSave = (appkey, value) => {
    if (value) {
      toSave.add({
        appkey,
        value,
        timestamp: new Date(),
      });
    }
  };

  /*
   * Following Line is no preferred but is required to parse encoded url correctly.
   * Simultaneous decoding of the equal sign results in a long string instead of an object.
   * It may have been fixed in v6 of query-string but can't upgrade because IE11 isn't supported
   */
  const decodedQueryString = decodeUriComponent(window.location.search);
  const parsed = queryString.parse(decodedQueryString);
  const { persistentKey, email, accountInfo } = parsed;

  addToSave(parameterOptions.persistentKey, persistentKey);
  addToSave(parameterOptions.email, email);
  addToSave('param_accountInfo', accountInfo);

  /*
   * If persistentKey is found, we want to save it as customerId == true for the online consumer guide
   */
  if(persistentKey) {
    addToSave('customerType', true)
  }

  /*
   * Parse Account Info Section (products, balances)
   */
  if (accountInfo) {
    logger.info('Attempting to parse Account Info parameter.', accountInfo);

    try {
      enactProductBalanceMap = accountInfo
        .replace(/\{/g, '[')
        .replace(/\}/g, ']');
      enactProductBalanceMap = JSON.parse(enactProductBalanceMap);

      logger.info('Successfully parsed account info object', enactProductBalanceMap);
    } catch (ex) {
      try {
        logger.info('Unable to parse account info object as JSON.  Attempting to parse as base64.');
        enactProductBalanceMap = JSON.parse(window.atob(enactProductBalanceMap));
      } catch (ex) {
        logger.error('Unable to parse account info object as base64.  Aborting saving account info parameters.');
      }
    }
  }

  /*
   * If Account Info is parsable extract products and balances
   */

  if (enactProductBalanceMap) {
    const {
      existingCustomerProducts,
      existingCustomerBalances,
    } = mapEnactProductsToIgniteProducts(enactProductBalanceMap);

    // Add Existing Customer Products to Answer Service
    addToSave(parameterOptions.accountInfo, existingCustomerProducts);

    // Add Existing Customer Balances to Answer Service
    if (existingCustomerBalances.size) {
      existingCustomerBalances.forEach((value, key) => addToSave(key, value));
    }
  }

  /*
   * Save all parsed values to Answer Service
   */
  logger.info('Saving customer information', toSave);
  const promises = [...toSave].map((answer) => {
    return ignite.answerService.saveAnswer(answer);
  });
  await Promise.all(promises);
  logger.info('Finished saving customer information');
};

export default saveParameters;
